import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { fetchReviews } from "../lib/api";
import ReviewsSlider from "../components/ReviewsSlider";
import ContactSection from "../components/ContactSection";

export default function Forside() {
  const [reviews, setReviews] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    {
      tag: "Mere end 40 års erfaring",
      title: "En vej til åbenhed og forståelse",
      description: "Skal jeg hjælpe dig med at bryde dine gamle mønstre og vaner med hypnose, eller vise dig vejen til åbenhed og forståelse med clairvoyance?",
      ctaText: "Bestil Clairvoyance",
      ctaLink: "/clairvoyance-online",
      secondaryText: "Læs om Hypnose",
      secondaryLink: "/hypnose",
      image: "https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/a3d6ce76-96f3-4371-8c41-b2ca08e435bb/john-calmar-clairvoyant-shaman-portraet.jpeg",
      imageAlt: "John Calmar – Erfaren clairvoyant og indviet shaman i Viby J ved Aarhus",
      subtitle: "John Calmar",
      subrole: "Shaman, Clairvoyant & Hypnoterapeut"
    },
    {
      tag: "Professionel Hypnoterapi",
      title: "Bryd dine gamle mønstre og vaner",
      description: "Gennem dybdegående hypnose og hypnoterapi hjælper jeg dig med at forløse ubevidste blokeringer, angst, fobier eller uhensigtsmæssige vaner.",
      ctaText: "Læs om Hypnose",
      ctaLink: "/hypnose",
      secondaryText: "Bestil Clairvoyance",
      secondaryLink: "/clairvoyance-online",
      image: "https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/a3d6ce76-96f3-4371-8c41-b2ca08e435bb/hypnose-hypnoterapi-behandling.png",
      imageAlt: "Hypnose og hypnoterapi til livsstilsændring og vaner hos John Calmar",
      subtitle: "John Calmar",
      subrole: "Certificeret Hypnoterapeut"
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  useEffect(() => {
    async function loadReviews() {
      const data = await fetchReviews();
      setReviews(data);
    }
    loadReviews();
  }, []);

  return (
    <>
      <Helmet>
        <title>Clairvoyant & Shaman John Calmar | Clairvoyance & Hypnose i Aarhus</title>
        <meta
          name="description"
          content="Få professionel clairvoyant rådgivning, hypnose og hypnoterapi hos John Calmar i Viby J ved Aarhus samt online. Mere end 40 års erfaring med klarhed og balance."
        />
        <link rel="canonical" href="https://johncalmar.dk/" />
        <meta property="og:title" content="Clairvoyant & Shaman John Calmar | Clairvoyance & Hypnose i Aarhus" />
        <meta
          property="og:description"
          content="Få professionel clairvoyant rådgivning, hypnose og hypnoterapi hos John Calmar i Viby J ved Aarhus samt online. Mere end 40 års erfaring med klarhed og balance."
        />
        <meta
          property="og:image"
          content="https://assets.auxodev.dk/assets/a3d6ce76-96f3-4371-8c41-b2ca08e435bb/john-calmar-clairvoyant-shaman-portraet.jpeg"
        />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Clairvoyant & Shaman John Calmar | Clairvoyance & Hypnose i Aarhus" />
        <meta
          name="twitter:description"
          content="Få professionel clairvoyant rådgivning, hypnose og hypnoterapi hos John Calmar i Viby J ved Aarhus samt online."
        />
        <meta
          name="twitter:image"
          content="https://assets.auxodev.dk/assets/a3d6ce76-96f3-4371-8c41-b2ca08e435bb/john-calmar-clairvoyant-shaman-portraet.jpeg"
        />
      </Helmet>

      {/* Hero Section */}
      <section className="relative w-full min-h-[85vh] flex flex-col lg:flex-row overflow-hidden bg-[#e2d9d0]" data-dev-id="src/pages/Forside.jsx:40:6">
        {/* Left Column (40% width, overlapping the image with a curve on the right side) */}
        <div className="relative w-full lg:w-[42%] min-h-[50vh] lg:min-h-[85vh] flex flex-col justify-center px-6 sm:px-12 md:px-16 lg:pl-[max(24px,calc((100vw-1400px)/2+24px))] lg:pr-16 py-12 lg:py-16 z-10 bg-[#e2d9d0] border-r border-[#d5c9be] rounded-tr-none lg:rounded-tr-[180px] rounded-br-[60px] lg:rounded-br-none rounded-bl-none shadow-2xl -mb-12 lg:mb-0">
          {/* Faint background illustration */}
          <svg
            className="absolute inset-0 w-full h-full text-neutral-950/5 pointer-events-none select-none"
            viewBox="0 0 100 100"
            fill="none"
            stroke="currentColor"
            strokeWidth="0.2"
          >
            <circle cx="50" cy="50" r="40" strokeDasharray="1 2" />
            <circle cx="50" cy="50" r="30" />
            <circle cx="50" cy="50" r="20" strokeDasharray="2 2" />
            <line x1="50" y1="10" x2="50" y2="90" />
            <line x1="10" y1="50" x2="90" y2="50" />
            <line x1="21.7" y1="21.7" x2="78.3" y2="78.3" />
            <line x1="21.7" y1="78.3" x2="78.3" y2="21.7" />
          </svg>

          {/* Text Content Area */}
          <div key={currentSlide} className="space-y-6 max-w-md mx-auto lg:mx-0 text-left relative z-10 animate-fade-in">
            <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-neutral-800">
              {slides[currentSlide].tag}
            </div>
            
            <h1 className="font-sans font-bold text-4xl sm:text-5xl lg:text-4xl xl:text-5xl text-neutral-950 tracking-tight leading-tight">
              {slides[currentSlide].title}
            </h1>
            
            <p className="font-sans font-normal text-base sm:text-lg text-neutral-800 leading-relaxed">
              {slides[currentSlide].description}
            </p>

            <div className="flex flex-col sm:flex-row lg:flex-row xl:flex-col gap-3 pt-3">
              <Link
                to={slides[currentSlide].ctaLink}
                className="inline-flex items-center justify-center gap-2 px-5 py-3 md:px-6 md:py-3.5 bg-neutral-950 hover:bg-neutral-900 text-white font-sans font-bold text-xs uppercase tracking-wider rounded-lg transition-all duration-200 shadow-lg cursor-pointer whitespace-nowrap"
              >
                {slides[currentSlide].ctaText}
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to={slides[currentSlide].secondaryLink}
                className="inline-flex items-center justify-center gap-2 px-5 py-3 md:px-6 md:py-3.5 border-2 border-neutral-950 hover:opacity-80 text-neutral-950 font-sans font-bold text-xs uppercase tracking-wider rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                {slides[currentSlide].secondaryText}
              </Link>
            </div>
          </div>
        </div>

        {/* Right Column (Image goes underneath the curved left column) */}
        <div className="relative lg:absolute lg:inset-y-0 lg:right-0 lg:left-[20%] w-full lg:w-auto h-[60vh] sm:h-[50vh] lg:h-full z-0 overflow-hidden bg-[#e2d9d0] lg:mt-0">
          <div key={currentSlide} className="absolute inset-0 w-full h-full animate-fade-in">
            <img
              src={slides[currentSlide].image}
              alt={slides[currentSlide].imageAlt}
              className="w-full h-full object-cover object-center md:object-[25%_center]"
            />
            {/* Subtle overlay */}
            <div className="absolute inset-0 bg-gradient-to-r from-neutral-950/30 via-neutral-950/10 to-transparent pointer-events-none z-10" />
          </div>
        </div>

      </section>

      {/* USP Bar */}
      <section className="border-t border-neutral-200 bg-neutral-100 py-12">
        <div className="max-w-[1400px] mx-auto px-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          <Link to="/om-john" className="flex flex-col items-center text-center space-y-2 group">
            <span className="font-display text-2xl md:text-3xl font-light text-neutral-900 group-hover:text-brand-700 transition-colors">40+ Års erfaring</span>
            <span className="text-xs text-neutral-600">Dyb faglig indsigt og visdom</span>
          </Link>

          <Link to="/clairvoyance-online" className="flex flex-col items-center text-center space-y-2 group">
            <span className="font-display text-2xl md:text-3xl font-light text-neutral-900 group-hover:text-brand-700 transition-colors">Medfødte evner</span>
            <span className="text-xs text-neutral-600">Forbindelse siden barndommen</span>
          </Link>

          <Link to="/shamanisme" className="flex flex-col items-center text-center space-y-2 group">
            <span className="font-display text-2xl md:text-3xl font-light text-neutral-900 group-hover:text-brand-700 transition-colors">Indviet shaman</span>
            <span className="text-xs text-neutral-600">Arbejde med naturens symboler</span>
          </Link>

          <Link to="/hypnose" className="flex flex-col items-center text-center space-y-2 group">
            <span className="font-display text-2xl md:text-3xl font-light text-neutral-900 group-hover:text-brand-700 transition-colors">Trygt forløb</span>
            <span className="text-xs text-neutral-600">Rolig og behagelig atmosfære</span>
          </Link>
        </div>
      </section>

      {/* Services Section */}
      <section className="bg-white py-24 border-b border-neutral-200/50">
        <div className="max-w-[1400px] mx-auto px-6">
          {/* Header Block */}
          <div className="text-left max-w-4xl">
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-brand-700 mb-3">
              Hvordan kan jeg hjælpe dig videre?
            </span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-light text-neutral-950 uppercase tracking-wide leading-tight mb-6">
              Clairvoyant ved Aarhus, få personlig vejledning hos John Calmar
            </h2>
            <p className="text-neutral-700 font-light leading-relaxed text-base md:text-lg">
              Jeg er John Calmar og har arbejdet som clairvoyant i mere end 40 år. I min praksis i Viby J ved Aarhus møder jeg dig med ro, respekt og nærvær. Her kan du få <Link to="/clairvoyance-online" className="underline decoration-brand-600/40 hover:decoration-brand-600 underline-offset-4 font-normal text-neutral-900 transition-colors">clairvoyant rådgivning</Link>, <Link to="/hypnose" className="underline decoration-brand-600/40 hover:decoration-brand-600 underline-offset-4 font-normal text-neutral-900 transition-colors">hypnose</Link> og spirituel vejledning, der tager udgangspunkt i dine spørgsmål og det, som fylder i dit liv.
            </p>
          </div>

          {/* Feature Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12 mt-16 pb-16 border-b border-neutral-200/70">
            {/* Item 1: Clairvoyance */}
            <div className="border-l border-brand-600 pl-6 py-2 flex flex-col justify-between h-full">
              <div>
                <h3 className="text-lg font-semibold text-neutral-950 uppercase tracking-wide mb-3">
                  Clairvoyance
                </h3>
                <p className="text-sm text-neutral-600 font-light leading-relaxed mb-6">
                  Få kærlige og omsorgsfulde budskaber fra dine åndelige guider gennem mine medfødte evner.
                </p>
              </div>
              <Link
                to="/clairvoyance-online"
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-600 hover:text-brand-700 transition-colors uppercase tracking-wider group mt-auto"
              >
                Læs mere
                <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>

            {/* Item 2: Hypnoterapi */}
            <div className="border-l border-brand-600 pl-6 py-2 flex flex-col justify-between h-full">
              <div>
                <h3 className="text-lg font-semibold text-neutral-950 uppercase tracking-wide mb-3">
                  Hypnoterapi
                </h3>
                <p className="text-sm text-neutral-600 font-light leading-relaxed mb-6">
                  Bryd uhensigtsmæssige mønstre og vaner effektivt gennem direkte arbejde med underbevidstheden.
                </p>
              </div>
              <Link
                to="/hypnose"
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-600 hover:text-brand-700 transition-colors uppercase tracking-wider group mt-auto"
              >
                Læs mere
                <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>

            {/* Item 3: Kurser & Workshops */}
            <div className="border-l border-brand-600 pl-6 py-2 flex flex-col justify-between h-full">
              <div>
                <h3 className="text-lg font-semibold text-neutral-950 uppercase tracking-wide mb-3">
                  Kurser & Workshops
                </h3>
                <p className="text-sm text-neutral-600 font-light leading-relaxed mb-6">
                  Styrk dine egne evner inden for clairvoyance, meditation og personlig udvikling.
                </p>
              </div>
              <Link
                to="/kurser-workshop"
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-600 hover:text-brand-700 transition-colors uppercase tracking-wider group mt-auto"
              >
                Læs mere
                <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>

            {/* Item 4: Shamanisme */}
            <div className="border-l border-brand-600 pl-6 py-2 flex flex-col justify-between h-full">
              <div>
                <h3 className="text-lg font-semibold text-neutral-950 uppercase tracking-wide mb-3">
                  Shamanisme
                </h3>
                <p className="text-sm text-neutral-600 font-light leading-relaxed mb-6">
                  Forbind dig med naturens kræfter og symbolmateriale gennem shamanistisk visdom.
                </p>
              </div>
              <Link
                to="/shamanisme"
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-600 hover:text-brand-700 transition-colors uppercase tracking-wider group mt-auto"
              >
                Læs mere
                <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>

          {/* Uddybende SEO sektioner under boksene */}
          <div className="pt-20 border-t border-neutral-200/70">
            {/* Sektions-header for uddybende emner */}
            <div className="text-center max-w-2xl mx-auto mb-14">
              <span className="text-xs font-semibold tracking-widest text-brand-700 uppercase">
                Indsigt & Forståelse
              </span>
              <h3 className="text-2xl sm:text-3xl font-light text-neutral-950 uppercase tracking-wide mt-2">
                Fordybelse i mit arbejde og metoder
              </h3>
              <div className="w-12 h-px bg-brand-600/40 mx-auto mt-4" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {/* Kort 1: Hvad kan clairvoyant rådgivning give dig? */}
              <div className="group relative bg-[#f4ede4]/60 hover:bg-[#f4ede4] border border-[#e5dcd1] hover:border-brand-600/40 rounded-xl p-7 sm:p-8 flex flex-col justify-between transition-all duration-300 shadow-sm hover:shadow-md">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="w-10 h-10 rounded-lg bg-[#e8ded2] text-brand-700 flex items-center justify-center transition-colors group-hover:bg-brand-600 group-hover:text-white">
                      <i className="fa-sharp fa-light fa-sparkles text-lg" aria-hidden="true" />
                    </div>
                    <span className="text-[11px] font-medium tracking-widest uppercase text-neutral-400">
                      01 / Indsigt
                    </span>
                  </div>
                  <h4 className="text-lg font-normal text-neutral-950 uppercase tracking-wide leading-snug pt-1">
                    Hvad kan clairvoyant rådgivning give dig?
                  </h4>
                  <div className="text-neutral-600 font-light text-sm leading-relaxed space-y-3">
                    <p>
                      Clairvoyant rådgivning kan være relevant, når du står ved en skillevej, savner overblik eller ønsker et nyt perspektiv på en relation. Vi kan se på kærlighed, familie, arbejde, sorg og personlig udvikling. Målet er ikke at vælge for dig, men at give dig indsigt, som kan gøre dine egne beslutninger mere tydelige.
                    </p>
                    <p className="text-neutral-500 text-[13px] border-t border-[#e2d7cb] pt-3">
                      En clairvoyance kan tage udgangspunkt i fortid, nutid og mulige retninger fremover. Sessionen kan også rumme auraindsigt, åndelige guider, tidligere liv eller afdødekontakt, når det viser sig som en del af min spirituelle tilgang. Indholdet varierer, fordi hver samtale følger den person og situation, jeg møder.
                    </p>
                  </div>
                </div>
                <div className="pt-5 mt-4 border-t border-[#e5dcd1]">
                  <Link
                    to="/clairvoyance-online"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-700 hover:text-brand-800 uppercase tracking-wider transition-colors"
                  >
                    Læs om clairvoyance
                    <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>

              {/* Kort 2: Hvad er clairvoyance hos mig? */}
              <div className="group relative bg-[#f4ede4]/60 hover:bg-[#f4ede4] border border-[#e5dcd1] hover:border-brand-600/40 rounded-xl p-7 sm:p-8 flex flex-col justify-between transition-all duration-300 shadow-sm hover:shadow-md">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="w-10 h-10 rounded-lg bg-[#e8ded2] text-brand-700 flex items-center justify-center transition-colors group-hover:bg-brand-600 group-hover:text-white">
                      <i className="fa-sharp fa-light fa-eye text-lg" aria-hidden="true" />
                    </div>
                    <span className="text-[11px] font-medium tracking-widest uppercase text-neutral-400">
                      02 / Klarsyn
                    </span>
                  </div>
                  <h4 className="text-lg font-normal text-neutral-950 uppercase tracking-wide leading-snug pt-1">
                    Hvad er clairvoyance hos mig?
                  </h4>
                  <div className="text-neutral-600 font-light text-sm leading-relaxed">
                    <p>
                      Clairvoyance bliver ofte beskrevet som klarsyn og en evne til at opfange information, som ikke er synlig på almindelig vis. I mit arbejde kommer indsigterne blandt andet som billeder, ord, stemninger og intuitive fornemmelser. Jeg formidler dem roligt og tydeligt, så du kan mærke, hvad der giver mening for dig.
                    </p>
                  </div>
                </div>
                <div className="pt-5 mt-4 border-t border-[#e5dcd1]">
                  <Link
                    to="/clairvoyance-online"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-700 hover:text-brand-800 uppercase tracking-wider transition-colors"
                  >
                    Sådan arbejder jeg
                    <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>

              {/* Kort 3: Hvordan foregår en clairvoyance? */}
              <div className="group relative bg-[#f4ede4]/60 hover:bg-[#f4ede4] border border-[#e5dcd1] hover:border-brand-600/40 rounded-xl p-7 sm:p-8 flex flex-col justify-between transition-all duration-300 shadow-sm hover:shadow-md">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="w-10 h-10 rounded-lg bg-[#e8ded2] text-brand-700 flex items-center justify-center transition-colors group-hover:bg-brand-600 group-hover:text-white">
                      <i className="fa-sharp fa-light fa-compass text-lg" aria-hidden="true" />
                    </div>
                    <span className="text-[11px] font-medium tracking-widest uppercase text-neutral-400">
                      03 / Mødeformer
                    </span>
                  </div>
                  <h4 className="text-lg font-normal text-neutral-950 uppercase tracking-wide leading-snug pt-1">
                    Hvordan foregår en clairvoyance?
                  </h4>
                  <div className="text-neutral-600 font-light text-sm leading-relaxed space-y-3">
                    <p>
                      Ønsker du clairvoyance ved Aarhus, kan du møde mig personligt i Viby J. Du kan også vælge online clairvoyance eller telefonisk clairvoyance. Den personlige kontakt og samtalen er i centrum, uanset hvordan vi mødes. Online og telefon gør det muligt at modtage vejledning hjemmefra og fra hele Danmark.
                    </p>
                    <p className="text-neutral-500 text-[13px] border-t border-[#e2d7cb] pt-3">
                      Du behøver ikke komme med en lang liste af spørgsmål. Det er ofte nok at have et tema eller en situation, du gerne vil forstå bedre. Inden vi begynder, taler vi om dine ønsker til sessionen, så rammen er tydelig, og der er plads til både tvivl, nysgerrighed og de spørgsmål, der opstår undervejs.
                    </p>
                  </div>
                </div>
                <div className="pt-5 mt-4 border-t border-[#e5dcd1]">
                  <Link
                    to="/clairvoyance-online"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-700 hover:text-brand-800 uppercase tracking-wider transition-colors"
                  >
                    Mødeformer & praktisk
                    <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>

              {/* Kort 4: Når du søger en dygtig clairvoyant, betyder tryghed noget */}
              <div className="group relative bg-[#f4ede4]/60 hover:bg-[#f4ede4] border border-[#e5dcd1] hover:border-brand-600/40 rounded-xl p-7 sm:p-8 flex flex-col justify-between transition-all duration-300 shadow-sm hover:shadow-md">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="w-10 h-10 rounded-lg bg-[#e8ded2] text-brand-700 flex items-center justify-center transition-colors group-hover:bg-brand-600 group-hover:text-white">
                      <i className="fa-sharp fa-light fa-shield-heart text-lg" aria-hidden="true" />
                    </div>
                    <span className="text-[11px] font-medium tracking-widest uppercase text-neutral-400">
                      04 / Tryghed
                    </span>
                  </div>
                  <h4 className="text-lg font-normal text-neutral-950 uppercase tracking-wide leading-snug pt-1">
                    Når du søger en dygtig clairvoyant, betyder tryghed noget
                  </h4>
                  <div className="text-neutral-600 font-light text-sm leading-relaxed space-y-3">
                    <p>
                      Når du søger en dygtig clairvoyant, handler valget ikke kun om erfaring. Du skal også kunne føle dig mødt og lyttet til. Jeg har mere end 40 års erfaring med clairvoyance og lægger vægt på ærlig formidling uden fordømmelse. Du får min fulde opmærksomhed i en rolig ramme, hvor der er plads til dig.
                    </p>
                    <p className="text-neutral-500 text-[13px] border-t border-[#e2d7cb] pt-3">
                      Som clairvoyant lover jeg ikke bestemte svar eller en fast fremtid. Jeg betragter rådgivningen som et perspektiv, du kan bruge i din egen proces. Du bevarer altid ansvaret for dine valg, og jeg opfordrer dig til at mærke efter. For mig er det en vigtig del af respektfuld og tryg clairvoyant rådgivning.
                    </p>
                  </div>
                </div>
                <div className="pt-5 mt-4 border-t border-[#e5dcd1]">
                  <Link
                    to="/om-john-calmar"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-700 hover:text-brand-800 uppercase tracking-wider transition-colors"
                  >
                    Om John Calmar
                    <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>

              {/* Kort 5: Hypnose, når du vil arbejde med vaner og mønstre */}
              <div className="group relative bg-[#f4ede4]/60 hover:bg-[#f4ede4] border border-[#e5dcd1] hover:border-brand-600/40 rounded-xl p-7 sm:p-8 flex flex-col justify-between transition-all duration-300 shadow-sm hover:shadow-md">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="w-10 h-10 rounded-lg bg-[#e8ded2] text-brand-700 flex items-center justify-center transition-colors group-hover:bg-brand-600 group-hover:text-white">
                      <i className="fa-sharp fa-light fa-brain text-lg" aria-hidden="true" />
                    </div>
                    <span className="text-[11px] font-medium tracking-widest uppercase text-neutral-400">
                      05 / Hypnoterapi
                    </span>
                  </div>
                  <h4 className="text-lg font-normal text-neutral-950 uppercase tracking-wide leading-snug pt-1">
                    Hypnose, når du vil arbejde med vaner og mønstre
                  </h4>
                  <div className="text-neutral-600 font-light text-sm leading-relaxed space-y-3">
                    <p>
                      Jeg tilbyder hypnose og hypnoterapi til dig, der ønsker støtte til at ændre vaner, reaktioner eller fastlåste mønstre. Det kan blandt andet handle om rygestop, sukkertrang, overspisning, bekymringer eller fobier. Vi tager udgangspunkt i dit mål og tilpasser forløbet til dig og dit tempo.
                    </p>
                    <p className="text-neutral-500 text-[13px] border-t border-[#e2d7cb] pt-3">
                      Hypnose er en tilstand med ro og fokuseret opmærksomhed, hvor vi arbejder med de mønstre, der ligger bag dine bevidste valg. Du sover ikke, og du mister ikke kontrollen. Vi taler altid om dine forventninger først. Hypnose er støtte til personlig forandring og erstatter ikke relevant lægefaglig behandling.
                    </p>
                  </div>
                </div>
                <div className="pt-5 mt-4 border-t border-[#e5dcd1]">
                  <Link
                    to="/hypnose"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-700 hover:text-brand-800 uppercase tracking-wider transition-colors"
                  >
                    Læs om hypnoseforløb
                    <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>

              {/* Kort 6: Vil du udforske clairvoyance gennem kurser og klarsyn? */}
              <div className="group relative bg-[#f4ede4]/60 hover:bg-[#f4ede4] border border-[#e5dcd1] hover:border-brand-600/40 rounded-xl p-7 sm:p-8 flex flex-col justify-between transition-all duration-300 shadow-sm hover:shadow-md">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="w-10 h-10 rounded-lg bg-[#e8ded2] text-brand-700 flex items-center justify-center transition-colors group-hover:bg-brand-600 group-hover:text-white">
                      <i className="fa-sharp fa-light fa-graduation-cap text-lg" aria-hidden="true" />
                    </div>
                    <span className="text-[11px] font-medium tracking-widest uppercase text-neutral-400">
                      06 / Kurser
                    </span>
                  </div>
                  <h4 className="text-lg font-normal text-neutral-950 uppercase tracking-wide leading-snug pt-1">
                    Vil du udforske clairvoyance gennem kurser og klarsyn?
                  </h4>
                  <div className="text-neutral-600 font-light text-sm leading-relaxed space-y-3">
                    <p>
                      Ud over personlige sessioner tilbyder jeg kurser og workshops om aura, intuition, meditation og shamanisme. Her arbejder du med egne oplevelser og konkrete øvelser i et respektfuldt fællesskab. Tilbuddene henvender sig både til dig, der er nysgerrig, og dig, der ønsker mere fordybelse.
                    </p>
                    <p className="text-neutral-500 text-[13px] border-t border-[#e2d7cb] pt-3">
                      Du kan også deltage i online klarsyn og klarsynsaftener eller udforske Calmars Univers. Formaterne giver mulighed for at møde det spirituelle arbejde på en anden måde end i en individuel clairvoyance. Aktuelle muligheder og praktiske oplysninger finder du på de relevante sider her på hjemmesiden.
                    </p>
                  </div>
                </div>
                <div className="pt-5 mt-4 border-t border-[#e5dcd1]">
                  <Link
                    to="/kurser-workshop"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-700 hover:text-brand-800 uppercase tracking-wider transition-colors"
                  >
                    Se aktuelle kurser & klarsyn
                    <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>
            </div>

            {/* Afsluttende boks: Er du klar til at tage det næste skridt? */}
            <div className="mt-14 bg-[#e2d9d0] border border-[#d5c9be] rounded-2xl p-8 sm:p-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
              <div className="max-w-3xl space-y-3 text-left">
                <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-neutral-800">
                  Næste skridt
                </span>
                <h3 className="text-2xl sm:text-3xl font-light text-neutral-950 uppercase tracking-wide leading-tight">
                  Er du klar til at tage det næste skridt?
                </h3>
                <p className="text-neutral-700 font-light text-sm sm:text-base leading-relaxed">
                  Er du i tvivl om, hvorvidt clairvoyance, clairvoyant rådgivning eller hypnose passer bedst til dit behov, er du velkommen til at tage kontakt. Sammen kan vi afklare, hvilken session der giver bedst mening for dig. Jeg ser frem til at møde dig personligt i Viby J, online eller over telefonen.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto shrink-0">
                <a
                  href="#kontakt"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-neutral-950 hover:bg-neutral-900 text-white font-sans font-bold text-xs uppercase tracking-wider rounded-lg transition-all duration-200 shadow-md cursor-pointer whitespace-nowrap"
                >
                  Kontakt mig
                  <ArrowRight className="w-4 h-4" />
                </a>
                <Link
                  to="/clairvoyance-online"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3.5 border-2 border-neutral-950 hover:bg-neutral-950/10 text-neutral-950 font-sans font-bold text-xs uppercase tracking-wider rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap"
                >
                  Bestil tid
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Storytelling Section */}
      <section className="py-12 md:py-16 bg-[#e2d9d0] border-y border-[#d5c9be]">
        <div className="max-w-[1400px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          <div className="lg:col-span-4">
            <img
              src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/a3d6ce76-96f3-4371-8c41-b2ca08e435bb/john-calmar-shaman-visdom-tromme.jpg"
              alt="John Calmar med shamantromme – naturvisdom og spirituel vejledning"
              className="w-full max-w-sm mx-auto lg:max-w-none h-auto object-cover"
            />
          </div>

          <div className="lg:col-span-8 text-left space-y-4">
            <h3 className="text-3xl md:text-4xl font-light text-neutral-950 leading-tight">
              Min terapeutiske udvikling startede, da jeg blev 25 år
            </h3>
            <div className="text-neutral-600 font-light space-y-3 leading-relaxed text-sm md:text-base">
              <p>
                Jeg er født i 1956. Mine clairvoyante evner er medfødte. Jeg har siden barndommen følt, set og talt med ånder fra den anden verden. Ånderne har undervist mig steder i naturen, hvor jeg har hentet kraft gennem min opvækst.
              </p>
              <p>
                Som <Link to="/shamanisme" className="text-neutral-950 underline decoration-brand-600/50 hover:decoration-brand-700 font-normal">indviet shaman</Link> arbejder jeg udelukkende med naturens egne billeder og symbolmateriale, og formidler budskaber og kræfter fra den indianske måde at se livet på.
              </p>
            </div>
            <div className="pt-2">
              <Link
                to="/om-john"
                className="inline-flex items-center gap-2 text-brand-600 hover:text-brand-700 font-medium transition-colors group text-sm"
              >
                Læs hele min historie
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      {reviews.length > 0 && (
        <ReviewsSlider reviews={reviews} />
      )}

      {/* Contact Section */}
      <ContactSection />
    </>
  );
}
